import { ApolloQueryResult } from "@apollo/client";
import { Plural, t, Trans } from "@lingui/macro";
import { LocalShipping } from "@mui/icons-material";
import { Alert, Stack, Tooltip, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAuthState } from "@saleor/sdk";
import clsx from "clsx";
import { Form, Formik, FormikErrors } from "formik";
import _, { find } from "lodash";
import moment from "moment";
import { GetStaticPropsContext, InferGetStaticPropsType } from "next";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import numbro from "numbro";
import qs from "qs";
import React, { ReactElement, useEffect, useState } from "react";

import {
	CollectionBlock,
	Layout,
	ProductMediaCarousel,
	Spinner,
	VariantSelector,
} from "@/components";
import { useAccountAddress } from "@/components/account";
import CheckoutClientBanner from "@/components/contacts/CheckoutClientBanner";
import Custom404 from "@/components/Custom404";
import { ImageExpand } from "@/components/ImageExpand";
import ProductPersonalize from "@/components/personalize/ProductPersonalize";
import {
	getAttributesValidationMeta,
	normalizePreferenceValue,
} from "@/components/personalize/utils";
import NewArrivalBadge from "@/components/product/NewArrivalBadge";
import OnSaleBadge from "@/components/product/OnSaleBadge";
import PriceComponent from "@/components/product/PriceComponent";
import ProductDetailTabs from "@/components/product/ProductDetailTabs";
import { ProductPageSeo } from "@/components/seo/ProductPageSeo";
import useChannelContext from "@/components/shared/contexts/ChannelContext";
import { useSnackbar } from "@/components/shared/hooks";
import { cloneAddress } from "@/components/utils/address";
import { VideoExpand } from "@/components/VideoExpand";
import apolloClient from "@/lib/graphql";
import { notNullable } from "@/lib/util";
import {
	AddressTypeEnum,
	CheckoutDetailsFragment,
	CheckoutError,
	Product,
	ProductDetailsFragment,
	ProductMediaFragment,
	ProductPathsDocument,
	ProductPathsQuery,
	ProductVariantFragment,
	useCheckoutBulkCreateMutation,
	useCreateCheckoutMutation,
	useProductBySlugQuery,
	User,
	useShippingMethodsQuery,
	useShippingZonesQuery,
	useUserDraftCheckoutsQuery,
} from "@/saleor/api";

interface FormValues {
	[key: string]: string | null | undefined;
}

const DefaultFormat = "DD-MMM-YYYY";

const ProductPage = ({ slug }: InferGetStaticPropsType<typeof getStaticProps>) => {
	const contentTabsRef = React.useRef<ProductDetailTabs>(null);
	const router = useRouter();
	const { user, authenticated } = useAuthState();
	const { channel } = useChannelContext();
	const [showDeliveryDate, setShowDeliveryDate] = useState<boolean>(true);

	const { loading: productLoading, data: productData } = useProductBySlugQuery({
		variables: { slug: slug || "", channel: channel },
		skip: !slug,
		fetchPolicy: "cache-and-network",
	});

	const [createCheckout, { loading: singleAddLoading }] = useCreateCheckoutMutation({
		// refetchQueries: [
		// 	{
		// 		query: UserDraftCheckoutsDocument,
		// 		variables: { first: 100, channel, multipleCheckout: false },
		// 	},
		// ],
		// awaitRefetchQueries: false,
	});
	const [bulkCreateCheckout, { loading: bulkCheckoutLoading }] = useCheckoutBulkCreateMutation({
		// refetchQueries: [
		// 	{
		// 		query: UserDraftCheckoutsDocument,
		// 		variables: { first: 100, channel, multipleCheckout: false },
		// 	},
		// ],
		// awaitRefetchQueries: false,
	});

	const {
		data: draftData,
		loading,
		refetch: refetchCheckouts,
	} = useUserDraftCheckoutsQuery({
		variables: { first: 100, multipleCheckout: false },
		skip: !authenticated || typeof window === "undefined",
		fetchPolicy: "network-only",
	});
	const [getAccountAddress, addressLoading] = useAccountAddress();
	const draftCheckouts = draftData?.userDraftCheckouts?.edges?.map((edge) => edge.node) || [];
	const clients = draftCheckouts.map((c) => c.profile).filter((n) => !!n);

	const [addToCartError, setAddToCartError] = useState("");
	const [expandedImage, setExpandedImage] = useState<ProductMediaFragment | undefined>(undefined);
	const [videoToPlay, setVideoToPlay] = useState<ProductMediaFragment | undefined>(undefined);
	const [isExternalProduct, setIsExternalProduct] = useState<boolean>(false);
	const [externalRedirectUrl, setExternalRedirectUrl] = useState<string | undefined>(undefined);
	const [buyNowButtonText, setBuyNowButtonText] = useState<string>(t`Buy Now`);
	const { showSnackbar } = useSnackbar();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

	const product = productData?.product;
	const productId = product?.id || null;

	const {
		data: shippingZones,
		loading: shippingZonesLoading,
		error: shippingZonesError,
	} = useShippingZonesQuery({
		skip: !productData?.product?.merchant?.id,
		variables: {
			channel,
			first: 10,
			filter: {
				merchant: productData?.product?.merchant?.id,
			},
		},
	});

	useEffect(() => {
		const isExternalProductStr = find(product && product?.metadata, {
			key: "external-product",
		});
		if (isExternalProductStr?.value === "true") {
			setIsExternalProduct(true);
		} else {
			setIsExternalProduct(false);
		}

		const externalRedirectUrlStr = find(product && product?.metadata, {
			key: "external-url",
		});
		if (externalRedirectUrlStr?.value) {
			setExternalRedirectUrl(externalRedirectUrlStr.value);
		} else {
			setExternalRedirectUrl(undefined);
		}

		const buyButtonTextStr = find(product && product?.metadata, {
			key: "buy-button-text",
		});
		if (buyButtonTextStr?.value) {
			setBuyNowButtonText(buyButtonTextStr?.value);
		} else {
			setBuyNowButtonText(t`Buy Now`);
		}
	}, [product?.metadata]);

	useEffect(() => {
		if (shippingZonesLoading) return;

		const shippingZone = shippingZones?.shippingZones?.edges[0];
		const deliveryDateHiddenStr = find(shippingZone && shippingZone?.node.metadata, {
			key: "delivery-date",
		});

		if (deliveryDateHiddenStr?.value === "hidden") {
			setShowDeliveryDate(false);
		} else {
			setShowDeliveryDate(true);
		}
	}, [shippingZones, shippingZonesLoading]);

	const {
		data: shippingMethods,
		loading: shippingMethodsLoading,
		error: shippingMethodsError,
	} = useShippingMethodsQuery({
		skip: !productData?.product?.merchant?.id,
		variables: {
			channel,
			first: 10,
			filter: {
				merchant: productData?.product?.merchant?.id,
				products: [productId],
			},
		},
	});

	const initialValues: FormValues = React.useMemo(() => {
		const returnValue =
			product?.attributes.reduce<FormValues>((a, c) => {
				if (c.attribute.id) {
					a[c.attribute.id] = "";
				}
				return a;
			}, {}) || {};
		//console.log(returnValue);
		return returnValue;
	}, [product?.attributes, shippingMethods?.shippingMethods?.edges]);

	if (productLoading) {
		return <Spinner />;
	}

	if (!product?.id) {
		return <Custom404 />;
	}

	const getSelectedVariantID = (
		queryVariant: string | undefined,
		product: ProductDetailsFragment
	) => {
		if (queryVariant) return queryVariant;
		else if (product?.variants?.length === 1) {
			if (typeof window !== "undefined") {
				router.replace({
					pathname: "/products/[slug]",
					query: { slug: product.slug, variant: product.variants![0]!.id! },
				});
			}
			return product.variants![0]!.id!;
		}
		return "";
	};

	// We have to check if code is run on the browser
	// before we can use the router
	const queryVariant =
		typeof window !== "undefined" ? router.query.variant?.toString() : undefined;

	const selectedVariantID = getSelectedVariantID(queryVariant, product);

	const selectedVariant = product?.variants?.find((v) => v?.id === selectedVariantID);

	/**
	 * If a variant has been selected by the user and this variant has media, return only those items.
	 * Otherwise, all product media are returned.
	 * @param   {ProductDetailsFragment} product  The product object
	 * @param   {ProductVariant} selectedVariant   The selected variant object
	 * @return  {ProductMediaFragment[]}   The media object that will be displayed to the user
	 */

	const getGalleryMedia = (
		product: ProductDetailsFragment,
		selectedVariant: ProductVariantFragment | undefined | null
	): ProductMediaFragment[] => {
		if (
			queryVariant &&
			selectedVariant?.id === queryVariant &&
			selectedVariant.media?.length !== 0
		)
			return selectedVariant?.media?.filter(notNullable) || [];
		return product?.media?.filter(notNullable) || [];
	};

	const media = getGalleryMedia(product, selectedVariant);

	/**
	 * When a variant is selected, the variant attributes are shown together with the attributes of the product. Otherwise, onyl the product
	 * attributes are shown
	 * @param   {ProductDetailsFragment} product  The product object
	 * @param   {ProductVariant} selectedVariant   The selected variant object
	 * @return  The attributes that will be shown to the user for the chosen product
	 */

	const getProductAttributes = (product: ProductDetailsFragment) => {
		return product.attributes;
	};

	const attributes = getProductAttributes(product);

	const doCreateCheckout = async (values: FormValues) => {
		const preferences = attributes
			.filter((attribute) => values[attribute.attribute.id])
			.map((attribute) => {
				return {
					attributeId: attribute.attribute.id,
					preferenceValue: values[attribute.attribute.id]!,
				};
			});
		const { data: createCheckoutData } = await createCheckout({
			variables: {
				checkout: {
					channel,
					email: user?.email || "anonymous@example.com",
					lines: [
						{
							quantity: 1,
							variantId: selectedVariantID,
							preferences,
						},
					],
					billingAddress: cloneAddress(getAccountAddress(AddressTypeEnum.Billing)),
					// selectedDeliveryDate: deliveryDateFormatted,
				},
			},
		});
		return createCheckoutData?.checkoutCreate?.errors?.filter((e) => !!e) || [];
	};

	const getPreferences = (checkout: CheckoutDetailsFragment, values: FormValues) => {
		if (!values) {
			return [];
		}
		return attributes
			.map((attribute) => {
				if (!attribute.attribute.id) {
					return null;
				}
				const value = normalizePreferenceValue(
					attribute.attribute,
					values[attribute.attribute.id],
					checkout.profile,
					user as User
				);
				if (!value) {
					return null;
				}
				return {
					attributeId: attribute.attribute.id,
					preferenceValue: value,
				};
			})
			.filter((c) => !!c)
			.map((c) => c!);
	};

	const doMultipleCheckoutCreate = async (values: FormValues) => {
		const checkouts = draftData?.userDraftCheckouts?.edges.map((e) => e.node);
		const checkoutAddress =
			checkouts?.find((a) => !!a.billingAddress)?.billingAddress ||
			getAccountAddress(AddressTypeEnum.Billing);
		const activeCheckouts = checkouts?.filter((c) => !!c.profile);
		const profileIds = activeCheckouts?.map((c) => c.profile!.id);
		const deliveryDateFormatted = showDeliveryDate
			? moment(values.deliveryDate, DefaultFormat).toDate()
			: null;
		const lines = activeCheckouts?.map((checkout) => {
			return {
				profileId: checkout.profile!.id,
				checkoutLines: [
					{
						quantity: 1,
						variantId: selectedVariantID,
						preferences: getPreferences(checkout, values),
					},
				],
				shipToAdvisor: false,
			};
		});
		const { data } = await bulkCreateCheckout({
			variables: {
				channel,
				profileIds,
				lines,
				billingAddress: cloneAddress(checkoutAddress),
				// selectedDeliveryDate: deliveryDateFormatted,
			},
		});
		return data?.checkoutBulkCreate?.errors?.filter((e) => !!e) || [];
	};

	const checkShouldBulkCheckout = (): boolean => {
		const numberOfCheckouts = draftData?.userDraftCheckouts?.totalCount || 0;
		const checkouts = draftData?.userDraftCheckouts?.edges.map((e) => e.node) || [];
		const hasMoreThanOneCheckout = numberOfCheckouts > 1;
		// More than one checkouts usually means that a contact has been assigned the user is probably just changing the product.
		if (hasMoreThanOneCheckout) return true;
		if (numberOfCheckouts === 1) {
			const checkout = checkouts[0];
			const hasProfile = checkout.profile?.id;
			return hasProfile !== undefined;
		}
		return false;
	};

	const onAddToCart = async (values: FormValues) => {
		setAddToCartError("");

		if (isExternalProduct) {
			if (externalRedirectUrl && externalRedirectUrl.length > 0) {
				window.open(
					externalRedirectUrl,
					externalRedirectUrl.startsWith("mailto:") ? "_self" : "_blank"
				);
			} else {
				showSnackbar({
					message: t`An unexpected error happened! Please try again later.`,
					severity: "error",
					horizontal: "center",
				});
			}
			return;
		}

		const errors: CheckoutError[] = checkShouldBulkCheckout()
			? await doMultipleCheckoutCreate(values)
			: await doCreateCheckout(values);

		if (errors.length === 0) {
			// Product successfully added, redirect to cart page
			router.push("/cart");
			return;
		}

		// Display error message
		const errorMessages =
			errors.map((e) => {
				return e.message || "";
			}) || [];
		setAddToCartError(errorMessages.join("\n"));
	};

	const loadingAddToCheckout = singleAddLoading || bulkCheckoutLoading;

	const isAddToCartButtonDisabled =
		!selectedVariant ||
		(selectedVariant?.stocks && selectedVariant?.stocks[0]?.quantity === 0) ||
		loadingAddToCheckout;

	const isNewArrival = product.collections?.find((c) => c?.slug === "new-arrivals");
	const isFreeDelivery = product.collections?.find((c) => c?.slug === "free-delivery");
	const isPersonalisable = product.collections?.find((c) => c?.slug === "personalise");

	const renderProductHints = () => {
		const isSocialEnterprise = product.collections?.find(
			(c) => c?.slug === "social-enterprise"
		);
		const isHalal = product.collections?.find((c) => c?.slug === "halal-muslim-friendly");
		const isVegan = product.collections?.find((c) => c?.slug === "vegan");
		const isVegetarian = product.collections?.find((c) => c?.slug === "vegetarian");
		const isNextDayDelivery = product.collections?.find((c) => c?.slug === "next-day-delivery");
		// const pricing = selectedVariant?.pricing || product.pricing;
		// const isOnSale = pricing?.onSale || false;
		if (
			!isPersonalisable &&
			!isSocialEnterprise &&
			!isHalal &&
			!isVegetarian &&
			!isVegan &&
			!isNextDayDelivery
		)
			return false;
		return (
			<div className="flex flex-row space-x-2 border px-4 py-2 border-gray-300 rounded-md items-center">
				<Stack
					direction={"row"}
					justifyContent={"flex-start"}
					flex={1}
					flexWrap={"nowrap"}
					spacing={2}
				>
					{isPersonalisable && (
						<Tooltip title={t`Personalise your Gift`}>
							<Stack direction={"column"} alignItems="center">
								<Image
									src="/icons/personalised-gift.svg"
									alt=""
									width={30}
									height={30}
								/>
								<Typography variant="body2">
									<Trans>Personalise</Trans>
								</Typography>
							</Stack>
						</Tooltip>
					)}
					{isSocialEnterprise && (
						<Tooltip title={t`Social Enterprise`}>
							<Stack direction={"column"} alignItems="center">
								<Image
									src="/icons/social-enterprise.svg"
									alt=""
									width={30}
									height={30}
								/>
								<Typography variant="body2">
									<Trans>Social Enterprise</Trans>
								</Typography>
							</Stack>
						</Tooltip>
					)}
					{isHalal && (
						<Tooltip title={t`Halal Certified`}>
							<Stack direction={"column"} alignItems="center">
								<Image src="/icons/halal.svg" alt="" width={30} height={30} />
								<Typography variant="body2">
									<Trans>Halal Certified</Trans>
								</Typography>
							</Stack>
						</Tooltip>
					)}
					{isVegan && (
						<Tooltip title={t`Vegan`}>
							<Stack direction={"column"} alignItems="center">
								<Image src="/icons/vegan.svg" alt="" width={30} height={30} />
								<Typography variant="body2">
									<Trans>Vegan</Trans>
								</Typography>
							</Stack>
						</Tooltip>
					)}
					{isVegetarian && (
						<Tooltip title={t`Vegetarian`}>
							<Stack direction={"column"} alignItems="center">
								<Image src="/icons/vegetarian.svg" alt="" width={30} height={30} />
								<Typography variant="body2">
									<Trans>Vegetarian</Trans>
								</Typography>
							</Stack>
						</Tooltip>
					)}
					{isNextDayDelivery && (
						<Tooltip title={t`Next Day Delivery`}>
							<Stack direction={"column"} alignItems="center">
								<Image
									src="/icons/next-day-delivery.svg"
									alt=""
									width={30}
									height={30}
								/>
								<Typography variant="body2">
									<Trans>Next Day Delivery</Trans>
								</Typography>
							</Stack>
						</Tooltip>
					)}
				</Stack>
			</div>
		);
	};

	const hasMedia = product.media && product.media.length > 0;

	const renderDeliveryTimeAndCost = () => {
		const shippingMethodEdges = shippingMethods?.shippingMethods?.edges;
		let minimumOrderPrice = null;
		let deliveryPrice = null;
		const warehouses = shippingMethodEdges && shippingMethodEdges[0].node.warehouses;
		const countries =
			warehouses &&
			warehouses.length > 0 &&
			warehouses.map((warehouse) => warehouse.countries);
		const countryNames =
			countries &&
			countries.length > 0 &&
			_.flatten(countries).map((country) => country && country.name);
		const stock =
			selectedVariant?.quantityAvailable && selectedVariant.quantityAvailable > 0
				? selectedVariant.quantityAvailable
				: null;

		if (shippingMethodEdges && shippingMethodEdges.length === 0) return;
		const defaultShipping = find(shippingMethodEdges, (method) => {
			const defaultMetaData = find(method.node.metadata, {
				key: "default",
			});
			return defaultMetaData?.value === "true";
		});
		const freeShipping = find(shippingMethodEdges, (method) => {
			const defaultMetaData = find(method.node.metadata, {
				key: "free",
			});
			return defaultMetaData?.value === "true";
		});
		minimumOrderPrice = (freeShipping && freeShipping.node.minimumOrderPrice?.amount) || null;
		deliveryPrice = (defaultShipping && defaultShipping.node.price?.amount) || null;
		return (
			<Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
				<div className="flex flex-col">
					{stock && (
						<Typography variant="body2">
							<Plural
								value={stock}
								zero="Out of stock!"
								_0="Out of stock!"
								one="# item in stock"
								other="# items in stock"
							/>
						</Typography>
					)}
				</div>
				{deliveryPrice && (
					<Stack direction="column">
						<Stack direction="row" justifyContent="flex-end" spacing={2}>
							<LocalShipping />
							<Typography variant="body2">
								{numbro(deliveryPrice).format("$0,0.00")}
							</Typography>
						</Stack>
						{minimumOrderPrice && (
							<Typography variant="body2">
								<Trans>
									Enjoy free delivery to one location for orders above{" "}
									{numbro(minimumOrderPrice).format("$0,0.00")}!
								</Trans>
							</Typography>
						)}
					</Stack>
				)}
			</Stack>
		);
	};

	const validateForm = (values: FormValues) => {
		if (Object.keys(initialValues).length === 0) {
			return {};
		}

		const errors: FormikErrors<FormValues> = {};
		const validationMeta = getAttributesValidationMeta(attributes.map((c) => c.attribute));

		attributes.forEach(({ attribute }) => {
			const validationItem = validationMeta[attribute.id];
			if (validationItem) {
				if (validationItem.required && !values[attribute.id]) {
					errors[attribute.id] = t`Field is required`;
				}
			}
		});
		return errors;
	};

	const renderFreeDelivery = () => {
		if (!isFreeDelivery) return false;
		return (
			<div className="flex text-yellow-500 font-semibold text-lg flex-row justify-end">
				<Trans>Free Delivery</Trans>
			</div>
		);
	};

	const stock: number =
		selectedVariant?.quantityAvailable && selectedVariant.quantityAvailable > 0
			? selectedVariant.quantityAvailable
			: 0;

	const renderForm = () => {
		return (
			<Formik<FormValues>
				initialValues={initialValues}
				onSubmit={onAddToCart}
				validate={validateForm}
				enableReinitialize
			>
				{({ values, setFieldValue }) => {
					return (
						<Form>
							<Stack spacing={2}>
								{selectedVariant && stock <= 0 && (
									// <p className="text-lg- text-yellow-600">Sold out!</p>
									<Alert severity="warning">
										<Trans>Sold Out!</Trans>
									</Alert>
								)}

								<VariantSelector
									product={product}
									selectedVariantID={selectedVariantID}
								/>
								<ProductPersonalize product={product} variant="product" />

								{!selectedVariant && authenticated && (
									<p className="text-lg- text-yellow-600">
										<Trans>Please choose an option</Trans>
									</p>
								)}
								{!!addToCartError && (
									<p className="text-red-600">{addToCartError}</p>
								)}

								{!shippingMethodsLoading &&
									!shippingMethodsError &&
									!isExternalProduct &&
									renderDeliveryTimeAndCost()}

								{renderFreeDelivery()}

								{authenticated ? (
									<Button
										type="submit"
										variant="contained"
										color="secondary"
										disabled={
											isAddToCartButtonDisabled ||
											!authenticated ||
											loading ||
											stock <= 0 ||
											clients.length > stock ||
											addressLoading
										}
										fullWidth
										disableElevation
										className="text-white"
									>
										{loadingAddToCheckout ? t`Adding...` : buyNowButtonText}
									</Button>
								) : (
									<Button
										variant="contained"
										color="secondary"
										disabled={loading}
										fullWidth
										disableElevation
										href={`/account/login${
											router.asPath !== "/"
												? qs.stringify(
														{ next: router.asPath },
														{ addQueryPrefix: true }
												  )
												: ""
										}`}
										className="text-white"
									>
										{t`Log In`}
									</Button>
								)}
								{clients.length > stock && selectedVariant && (
									<section className="col-span-3 text-center">
										<Stack
											spacing={2}
											alignItems="center"
											justifyContent="center"
										>
											<div>
												<div className="text-s font-semibold text-red-700">
													<Plural
														value={stock}
														zero="This product is out of stock."
														_0="This product is out of stock."
														one="There is only # item of the product
														in Stock."
														other="There are only # items of the product
														in Stock."
													/>
												</div>
											</div>
										</Stack>
									</section>
								)}
							</Stack>
						</Form>
					);
				}}
			</Formik>
		);
	};

	return (
		<>
			<ProductPageSeo product={product} />
			<main
				className={clsx(
					"max-h-full overflow-auto md:overflow-hidden mx-auto max-w-7xl pt-8 md:px-8 pb-8",
					expandedImage && "hidden",
					videoToPlay && "hidden"
				)}
			>
				<CheckoutClientBanner
					onClose={refetchCheckouts}
					selectedVariant={selectedVariant}
				/>
				<div
					className={clsx("grid grid-cols-1 gap-12 lg:gap-16 mt-6", {
						"lg:grid-cols-7": hasMedia,
					})}
				>
					{hasMedia && (
						<div
							className={clsx(
								"w-full max-h-screen col-span-1 lg:h-full relative lg:col-span-3"
							)}
						>
							<ProductMediaCarousel
								media={media}
								onMediaClick={(media) => setExpandedImage(media)}
							/>
							<Stack
								direction="row"
								spacing={1}
								className="absolute left-2 top-2"
								alignItems="flex-start"
							>
								{isNewArrival && <NewArrivalBadge />}
								{/* {isFreeDelivery && <FreeDeliveryBadge />} */}
								{product.id && <OnSaleBadge productId={product.id} />}
							</Stack>
							{/* <div className="absolute right-2 top-2">
							</div> */}
						</div>
					)}

					<div className="space-y-6 mt-10 lg:mt-0 col-span-1 px-8 lg:col-span-4">
						<div className="space-y-1">
							<h1 className="text-4xl font-bold tracking-tight text-gray-800">
								{product?.name}
							</h1>
							{product.merchant && (
								<Stack direction="row" spacing={1} pt={1}>
									<div>
										<Trans>Merchant:</Trans>
									</div>
									<Link
										href={{
											pathname: "/merchant/[slug]",
											query: { slug: product.merchant?.slug },
										}}
										passHref
									>
										<a>{product.merchant?.name}</a>
									</Link>
								</Stack>
							)}
						</div>

						{renderProductHints()}
						{/* <ProductFavoriteButton productId={product.id} variant="default" /> */}

						<PriceComponent
							product={product as Product}
							size="large"
							selectedVariantId={selectedVariantID}
						/>

						{renderForm()}
					</div>
				</div>
				<ProductDetailTabs ref={contentTabsRef} product={product} descriptionExpanded />
				{product.merchant && (
					<div className="mx-auto pt-20 px-8">
						<CollectionBlock
							iconName="balloons"
							title={t`More from merchant`}
							link={`/merchant/${product.merchant?.slug}`}
							filter={{
								merchants: product.merchant?.id ? [product.merchant?.id] : null,
							}}
						/>

						{/* <CollectionBlock
						iconName="balloons"
						title={t`Also bought`}
						link={`/merchant/${product.merchant?.slug}`}
						filter={{ merchants: product.merchant?.id ? [product.merchant?.id] : null }} /> */}
					</div>
				)}
			</main>

			{expandedImage && (
				<div className="absolute min-h-screen min-w-screen h-full w-full top-0 bottom-0 left-0 right-0 z-40">
					<ImageExpand
						image={expandedImage}
						onRemoveExpand={() => setExpandedImage(undefined)}
					/>
				</div>
			)}

			{videoToPlay && (
				<div className="absolute min-h-screen min-w-screen top-0 bottom-0 left-0 right-0 z-40">
					<VideoExpand
						video={videoToPlay}
						onRemoveExpand={() => setVideoToPlay(undefined)}
					/>
				</div>
			)}
		</>
	);
};

export default ProductPage;

export async function getStaticPaths() {
	const result: ApolloQueryResult<ProductPathsQuery | undefined> = await apolloClient.query({
		query: ProductPathsDocument,
		variables: {
			channel: "default-channel",
		},
	});
	const paths =
		result.data?.products?.edges.map(({ node }) => ({
			params: { slug: node.slug },
		})) || [];

	return {
		paths,
		fallback: true,
	};
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
	const productSlug = context.params?.slug?.toString();
	return {
		props: { slug: productSlug },
		revalidate: 60, // value in seconds, how often ISR will trigger on the server
	};
};

ProductPage.getLayout = function getLayout(page: ReactElement) {
	return <Layout>{page}</Layout>;
};
